<template>
  <div class="tag-page">
    <div class="tag-page__header glass-panel">
      <div class="tag-page__badge">Tag</div>
      <h1 class="tag-page__title">{{ tagName || 'All Tags' }}</h1>
      <p class="tag-page__description" v-if="tagName && articles.length">
        {{ articles.length }} article{{ articles.length !== 1 ? 's' : '' }} tagged with "{{ tagName }}"
      </p>
      <p class="tag-page__description" v-else-if="!tagName">
        {{ (allTags || []).length }} tags available
      </p>
      <p class="tag-page__description" v-else-if="tagName && !articles.length">
        No articles found with this tag
      </p>
    </div>

    <div class="tag-page__content">
      <!-- Tag list view (/tags/) -->
      <div v-if="!tagName" class="tag-page__tag-cloud">
        <a
          v-for="item in allTags"
          :key="item.tag"
          :href="`/tags/${encodeURIComponent(item.tag)}/`"
          class="tag-page__tag-item"
        >
          <span class="tag-page__tag-name">{{ item.tag }}</span>
          <span class="tag-page__tag-count">{{ item.count }}</span>
        </a>
      </div>

      <!-- Single tag article list (/tags/:tag/) -->
      <div v-else-if="articles.length" class="tag-page__grid">
        <ArticleCard
          v-for="article in articles"
          :key="article.slug"
          :article="article"
          class="tag-page__card"
        />
      </div>
    </div>

    <div class="tag-page__empty" v-else-if="tagName && !articles.length">
      <p>No articles found for this tag.</p>
      <router-link to="/" class="tag-page__back-link">Back to Home</router-link>
    </div>
  </div>
</template>

<script>
import ArticleCard from '@/components/article/ArticleCard.vue'
import { generateMetaTags } from '@/utils/seo'

export default {
  name: 'TagPage',
  components: {
    ArticleCard,
  },
  asyncData({ store, route, context }) {
    return store.dispatch('articles/loadAllArticles')
  },
  computed: {
    tagName() {
      return decodeURIComponent(this.$route.params.tag || '')
    },
    articles() {
      const tag = this.tagName
      return this.$store.getters['articles/getArticlesByTag'](tag)
    },
    allTags() {
      return this.$store.getters['articles/getAllTags']()
    },
  },
  methods: {
    generateMetaTags,
  },
  metaInfo() {
    const title = this.tagName ? `Articles tagged "${this.tagName}"` : 'All Tags'
    return generateMetaTags({
      title,
      description: this.tagName
        ? `Browse all articles tagged with ${this.tagName} on GameCenter`
        : 'Browse all tags on GameCenter',
      path: this.$route.path,
      type: 'website',
    })
  },
}
</script>

<style lang="scss" scoped>
@use "sass:color";
@use '@/css/variables.scss' as *;

@keyframes gradientBorder {
  0% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
}

@keyframes pulseGlow {
  0% { opacity: 0.6; transform: scale(1); }
  50% { opacity: 0.8; transform: scale(1.02); }
  100% { opacity: 0.6; transform: scale(1); }
}

.tag-page {
  max-width: 1200px;
  margin: 0 auto;
  padding: $spacing-xl $spacing-lg;

  &__header {
    position: relative;
    text-align: center;
    padding: $spacing-xxl $spacing-xl;
    margin-bottom: $spacing-xxl;
    background: rgba($bg-secondary, 0.65);
    backdrop-filter: blur(16px);
    -webkit-backdrop-filter: blur(16px);
    border-radius: $border-radius-lg;
    border: 1px solid rgba(255, 255, 255, 0.08);
    overflow: hidden;

    &::before {
      content: '';
      position: absolute;
      top: 50%;
      left: 50%;
      width: 400px;
      height: 400px;
      background: radial-gradient(circle, rgba($primary-color, 0.08) 0%, transparent 70%);
      transform: translate(-50%, -50%);
      pointer-events: none;
      animation: pulseGlow 4s ease-in-out infinite;
    }

    &::after {
      content: '';
      position: absolute;
      inset: 0;
      border-radius: $border-radius-lg;
      padding: 2px;
      background: linear-gradient(90deg, $primary-color, $secondary-color, $accent-color, $primary-color);
      background-size: 300% 300%;
      animation: gradientBorder 6s ease infinite;
      -webkit-mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
      -webkit-mask-composite: xor;
      mask-composite: exclude;
      opacity: 0.5;
      pointer-events: none;
    }
  }

  &__badge {
    display: inline-block;
    padding: $spacing-xs $spacing-md;
    background: linear-gradient(90deg, $primary-color, color.adjust($primary-color, $lightness: -10%));
    color: #fff;
    font-size: $font-size-xs;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 1px;
    border-radius: rem(20);
    margin-bottom: $spacing-md;
    position: relative;
    z-index: 1;
  }

  &__title {
    font-size: $font-size-xxxl;
    font-weight: 800;
    color: $text-primary;
    margin: 0 0 $spacing-md 0;
    letter-spacing: -1px;
    position: relative;
    z-index: 1;

    @include mobile {
      font-size: $font-size-xxl;
    }
  }

  &__description {
    font-size: $font-size-lg;
    color: $text-secondary;
    margin: 0;
    position: relative;
    z-index: 1;
  }

  &__grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: $spacing-lg;

    @media (max-width: 900px) {
      grid-template-columns: repeat(2, 1fr);
    }

    @media (max-width: 600px) {
      grid-template-columns: 1fr;
    }
  }

  &__card {
    @include premium-hover;
  }

  &__tag-cloud {
    display: flex;
    flex-wrap: wrap;
    gap: $spacing-sm;
    justify-content: center;
  }

  &__tag-item {
    display: inline-flex;
    align-items: center;
    gap: $spacing-xs;
    padding: $spacing-sm $spacing-md;
    background: rgba($bg-secondary, 0.6);
    border: 1px solid rgba(255, 255, 255, 0.08);
    border-radius: $border-radius;
    color: $text-primary;
    text-decoration: none;
    font-size: $font-size-sm;
    transition: all 0.2s ease;

    &:hover {
      border-color: rgba($primary-color, 0.4);
      background: rgba($primary-color, 0.1);
      transform: translateY(-2px);
    }
  }

  &__tag-name {
    font-weight: 600;
  }

  &__tag-count {
    font-size: $font-size-xs;
    color: $text-muted;
    background: rgba(255, 255, 255, 0.06);
    padding: 1px $spacing-xs;
    border-radius: rem(10);
  }

  &__empty {
    text-align: center;
    padding: $spacing-xxl 0;
    color: $text-secondary;

    p {
      font-size: $font-size-lg;
      margin-bottom: $spacing-lg;
    }
  }

  &__back-link {
    display: inline-flex;
    align-items: center;
    gap: $spacing-sm;
    padding: $spacing-sm $spacing-lg;
    background: $bg-secondary;
    border: 1px solid $border-color;
    border-radius: $border-radius;
    color: $text-primary;
    text-decoration: none;
    font-weight: 600;
    transition: background-color 0.2s, border-color 0.2s;

    &:hover {
      background: $bg-tertiary;
      border-color: $primary-color;
    }
  }
}
</style>
